import { format } from 'date-fns';
import { alpha } from 'material-hu';
import { Attachment, FormFile, FileTypes } from 'src/types/attachments';
import { FileExtensions } from 'src/constants/filesPreview';
import { sizeToBytes, bytesToSize } from 'src/utils/bytes';
import {
  SIGNS,
  IMAGE_COMPRESSION_QUALITY,
  IMAGE_CONVERT_TYPES,
  IMAGE_CONVERT_SIZE,
} from 'src/constants/attachments';
import { FileAsset } from 'src/types/serviceManagement';
import Compressor from 'compressorjs';
import pdfIcon from 'src/assets/pdf.svg';
import wordIcon from 'src/assets/word.svg';
import excelIcon from 'src/assets/excel.svg';
import gifIcon from 'src/assets/gif.svg';
import defaultFileIcon from 'src/assets/file.svg';
import defaultIcon from 'src/assets/defaultIcon.png';
import imageIcon from 'src/assets/imageIcon.png';
import videoIcon from 'src/assets/videoIcon.png';
import 'src/pages/dashboard/files/i18n';
import { uploadImage } from 'src/services/attachments';

export const getFileNameExtension = (fileName: string) =>
  fileName?.split('.').pop();

export const getTypeName = (type: string) => {
  switch (type) {
    case 'VIDEO':
      return 'video';
    case 'IMAGE':
      return 'image';
    default:
      return 'file';
  }
};

export const downloadFile = (data: BlobPart, title: string) => {
  const blob = new Blob([data]);
  const downloadUrl = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = title;

  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(downloadUrl);
};

export const megabytesToBytes = (megabytes: number) => megabytes * 1024 * 1024;

export const getSimplifiedFileName = (name: string, type: string) => {
  const extension = getFileNameExtension(name);
  const typeName = getTypeName(type);
  return `${typeName}.${extension}`;
};

export const getAttachment = (file): Attachment => ({
  url: (file.fileObject && URL.createObjectURL(file.fileObject)) || file.url,
  type: file.type,
  size: typeof file.size === 'string' ? file.size : bytesToSize(file.size),
  name: file.name,
  position: file.position,
  height: file.height || null,
  width: file.width || null,
  lastModified: (file.fileObject && file.fileObject.lastModified) || null,
});

export const getFormFileFromAttachment = (file: Attachment): FormFile => ({
  name: file.name,
  url: file.url,
  size: sizeToBytes(file.size),
  mime: file.type,
  fileObject: null,
  type: file.type,
  toUpload: false,
  height: file.height || null,
  width: file.width || null,
  position: file.position,
});

export const getNewFileForReplacement = (
  file: Attachment,
  data: any,
): FormFile => ({
  name: file?.name,
  size: data?.size,
  mime: data?.type,
  width: file?.width,
  height: file?.height,
  fileObject: data,
  type: FileTypes.IMAGE,
  toUpload: true,
});

export const getAttachmentFromPictureUrl = (url: string): Attachment => ({
  url,
  type: FileTypes.IMAGE,
  size: null,
  name: 'image',
  position: null,
  height: null,
  width: null,
});

export const getAttachmentFromFileUrl = (
  url: string,
  name: string = 'file',
) => ({
  url,
  type: FileTypes.FILE,
  size: null,
  name,
  position: null,
  height: null,
  width: null,
});

export const getBlob = async (url: string) => {
  try {
    const response = await fetch(url, { cache: 'reload' });
    const blob = await response.blob();

    return blob;
  } catch (err) {
    return null;
  }
};

export const getBlobUrl = (blob: any): string | null => {
  if (!blob) return null;

  return window.URL.createObjectURL(blob);
};

export const openBlob = (blob, blobUrl) => {
  const link = document.createElement('a');
  link.href = blobUrl;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.type = blob?.type;
  link.click();
};

export type QueryParams = {
  query?: string;
  order?: string;
  orderBy?: string;
  page?: number;
  limit?: number;
};

export const filesTableHeader = t => [
  {
    id: 'FILE_NAME',
    text: t('TITLE'),
  },
  {
    id: 'CREATOR',
    text: t('OWNER'),
  },
  {
    id: 'UPDATED_AT',
    text: t('UPDATED_AT'),
  },
  {
    id: 'SIZE',
    text: t('SIZE'),
  },
];

export const removeQueryParams = (url?: string) =>
  url ? url.split(SIGNS.QUESTION_MARK)[0] : '';

export const getFileIcon = (file, preview = false) => {
  const { name = '', url = '' } = file || {};
  const splitName = name
    ? name.split(SIGNS.DOT)
    : removeQueryParams(url).split('/').slice(-1).toString();
  const extension = splitName[splitName.length - 1]?.toLowerCase();
  switch (extension) {
    case FileExtensions.PDF:
      return pdfIcon;
    case FileExtensions.DOC:
    case FileExtensions.DOCX:
      return wordIcon;
    case FileExtensions.XLS:
    case FileExtensions.XLSX:
      return excelIcon;
    case FileExtensions.GIF:
      return gifIcon;
    case FileExtensions.PNG:
    case FileExtensions.JPG:
    case FileExtensions.JPEG:
      return imageIcon;
    case FileExtensions.MOV:
    case FileExtensions.MP4:
      return videoIcon;
    default:
      if (preview) {
        return defaultIcon;
      }
      return defaultFileIcon;
  }
};

export const isImageFile = (ext, type) =>
  type === FileTypes.IMAGE ||
  ext === FileExtensions.PNG ||
  ext === FileExtensions.JPG ||
  ext === FileExtensions.GIF ||
  ext === FileExtensions.JPEG;

export const isVideoFile = (ext, type) =>
  type === FileTypes.VIDEO ||
  ext === FileExtensions.MP4 ||
  ext === FileExtensions.MOV;

export const isPdf = ext => ext === FileExtensions.PDF;

export const isFileSupported = (ext, type) =>
  isImageFile(ext, type) || isVideoFile(ext, type) || isPdf(ext);

export const isFolder = type => type === FileTypes.FOLDER;

export const getScalePdf = value => {
  switch (value) {
    case 1:
      return 1.1;
    case 2:
      return 1.3;
    case 3:
      return 1.5;
    case 4:
      return 1.7;
    default:
      return 1;
  }
};

export const getDownloadName = (title: string, extension?: string) => {
  const date = format(new Date(), 'yyyy-MM-dd_HH-mm-ss');

  return title + date + extension;
};

const download = (url: string, title: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', title);

  link.click();
  link.remove();
};

export const downloadBlob = (data: BlobPart, title: string) => {
  const url = getBlobUrl(new Blob([data]));
  download(url, title);
};

export const downloadUrl = async (url: string, title: string) => {
  const blob = await getBlob(url);
  const blobUrl = getBlobUrl(blob);

  download(blobUrl, title);
};

export const fileToAttachment = (file: File) =>
  new Promise<FileAsset>(resolve => {
    const objectURL = URL.createObjectURL(file);
    const result = {
      url: null,
      previewUrl: null,
      previewType: null,
      file,
      name: file.name,
      size: bytesToSize(file.size),
      id: null,
    };

    if (isImage(file)) {
      const i = new Image();
      i.onload = () => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          resolve({
            ...result,
            type: FileTypes.IMAGE,
            width: i.width,
            height: i.height,
          });
        };
      };
      i.src = objectURL;
    } else if (isVideo(file)) {
      const v = document.createElement('video');
      v.src = objectURL;
      // eslint-disable-next-line func-names
      v.addEventListener('loadedmetadata', function () {
        resolve({
          ...result,
          type: FileTypes.VIDEO,
          width: this.videoWidth,
          height: this.videoHeight,
        });
      });
    } else {
      resolve({
        ...result,
        type: FileTypes.FILE,
        width: null,
        height: null,
      });
    }
  });

export const getArrayBuffer = (file: File) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });

export const isImage = (file: File) => file.type.includes('image');
export const isVideo = (file: File) => file.type.includes('video');

export const getContentType = (filename: string) => {
  switch (getFileNameExtension(filename)) {
    case 'ppsm':
      return 'application/vnd.ms-powerpoint.slideshow.macroenabled.12';
    case 'pptm':
      return 'application/vnd.ms-powerpoint.presentation.macroEnabled.12';
    case 'ppsx':
      return 'application/vnd.openxmlformats-officedocument.presentationml.slideshow';
    case 'xlsm':
      return 'application/vnd.ms-excel.sheet.macroEnabled';
    default:
      return '';
  }
};

export const blobToFile = (blob: Blob, filename: string) =>
  new File([blob], filename, { type: blob.type });

export const compressFile = async (file: File) =>
  new Promise<File>((resolve, reject) => {
    if (isImage(file) && !file.type.includes('gif')) {
      return new Compressor(file, {
        quality: IMAGE_COMPRESSION_QUALITY,
        convertTypes: IMAGE_CONVERT_TYPES,
        convertSize: IMAGE_CONVERT_SIZE,
        success: (compressedResult: File) =>
          resolve(blobToFile(compressedResult, compressedResult.name)),
        error: err => reject(err),
      });
    }
    return resolve(file);
  });

export const addFileType = (file: File) => {
  if (file.type) {
    return file;
  }

  return new File([file], file.name, { type: getContentType(file.name) });
};

export const getFileType = (extension: string) => {
  switch (extension?.toLowerCase()) {
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'webp':
      return FileTypes.IMAGE;
    case 'mpeg':
    case 'webm':
    case 'mov':
    case 'mp4':
    case 'avi':
      return FileTypes.VIDEO;
    /* case 'csv':
    case 'xls':
    case 'xlsx':
    case 'xlsm':
      return FileTypes.EXCEL;
    case 'pdf':
      return FileTypes.PDF;
    case 'pptx':
    case 'ppt':
    case 'ppsm':
    case 'ppsx':
      return FileTypes.PPT;
    case 'doc':
    case 'docx':
      return FileTypes.DOC; */
    case 'gif':
      return FileTypes.GIF;
    default:
      return FileTypes.FILE;
  }
};

export const getDropzoneStyles = (isDragActive, loadingFiles, theme) => {
  if (isDragActive) {
    return {
      border: `1px dashed ${theme.palette.primary.main}`,
      backgroundColor: `${alpha(theme.palette.primary.main, 0.08)}`,
    };
  } else if (loadingFiles.length > 0) {
    return {
      border: '1px dashed #0000001F',
      backgroundColor: '#f0f0f0',
      cursor: 'not-allowed',
    };
  } else {
    return {
      border: '1px dashed #0000001F',
      backgroundColor: '#FFFFFF',
    };
  }
};

export const preprocessImage = async (image: {
  file: File | null;
  url: string | null;
}) => {
  let imageUrl = image?.url ? image?.url.split('?')[0] : undefined;

  if (image?.file) {
    const bannerFile = image.file;
    const { url } = await uploadImage({
      name: bannerFile.name,
      size: bannerFile.size,
      mime: bannerFile.type,
      fileObject: bannerFile,
      type: FileTypes.IMAGE,
    });
    imageUrl = url;
  }
  return imageUrl;
};
